import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import scheduleService from '../../services/scheduleService';
import participantsService from '../../services/participantsService';
import callerService from '../../services/callerService';
import TailwindPageWrapper from "../generic/TailwindPageWrapper";

const CallerView = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  // Venue des Callers - wenn keine zugewiesen, kann der User wählen
  const assignedVenueFromUser = user?.venue;
  const [selectedVenue, setSelectedVenue] = useState(assignedVenueFromUser || "");
  const assignedVenue = assignedVenueFromUser || selectedVenue;
  
  const [loading, setLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [preparationEvents, setPreparationEvents] = useState([]);
  const [preparationEventsWithParticipants, setPreparationEventsWithParticipants] = useState([]);
  const [checklist, setChecklist] = useState(new Map()); // Map von groupId_participantId -> checked
  const [checklistData, setChecklistData] = useState(new Map()); // Backend-Daten: groupId -> Map(participantId -> data)

  useEffect(() => {
    if (assignedVenue) {
      setLoading(true);
      loadData();
      loadChecklistFromStorage();
      
      // Auto-Refresh alle 30 Sekunden
      const interval = setInterval(() => {
        loadData();
      }, 30000);
      
      return () => clearInterval(interval);
    } else {
      // Wenn keine venue ausgewählt ist, setze loading auf false
      setLoading(false);
    }
  }, [assignedVenue]);

  // Lade Checkliste vom Backend für alle Vorbereitungsgruppen
  useEffect(() => {
    const loadChecklistsFromBackend = async () => {
      if (preparationEventsWithParticipants.length === 0) return;
      
      const newChecklistData = new Map();
      
      for (const event of preparationEventsWithParticipants) {
        if (event.group?.id) {
          try {
            const result = await callerService.getChecklist(event.group.id);
            if (result.success && result.data) {
              const groupChecklist = new Map();
              result.data.forEach(item => {
                groupChecklist.set(item.participantId, item);
              });
              newChecklistData.set(event.group.id, groupChecklist);
            }
          } catch (error) {
            console.error(`Fehler beim Laden der Checkliste für Gruppe ${event.group.id}:`, error);
          }
        }
      }
      
      setChecklistData(newChecklistData);
    };
    
    loadChecklistsFromBackend();
  }, [preparationEventsWithParticipants]);

  // Lade Checkliste aus Local Storage
  const loadChecklistFromStorage = () => {
    const stored = new Map();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('caller_checklist_')) {
        try {
          const value = JSON.parse(localStorage.getItem(key));
          stored.set(key, value);
        } catch (e) {
          console.error('Fehler beim Laden der Checkliste:', e);
        }
      }
    }
    setChecklist(stored);
  };

  // Speichere Checkliste-Status im Local Storage
  const saveChecklistItem = (groupId, participantId, checked) => {
    const key = `caller_checklist_${groupId}_${participantId}`;
    const value = {
      checked,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(key, JSON.stringify(value));
    
    setChecklist(prev => {
      const newMap = new Map(prev);
      newMap.set(key, value);
      return newMap;
    });
  };

  // Prüfe ob Athlet abgehakt ist (Backend hat Priorität)
  const isParticipantChecked = (groupId, participantId) => {
    // Prüfe zuerst Backend-Daten
    const groupChecklist = checklistData.get(groupId);
    if (groupChecklist) {
      const item = groupChecklist.get(participantId);
      if (item) {
        return item.checkedByCaller || false;
      }
    }
    
    // Fallback zu Local Storage
    const key = `caller_checklist_${groupId}_${participantId}`;
    const item = checklist.get(key);
    return item ? item.checked : false;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Lade Schedule-Events für die zugewiesene Fläche
      const scheduleResult = await scheduleService.getAllScheduleEvents({ venue: assignedVenue });
      const scheduleEvents = scheduleResult.success ? scheduleResult.data || [] : [];
      
      // Sortiere nach Startzeit
      scheduleEvents.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      
      // Finde aktuelles Event (Status "Aktiv")
      const activeEvent = scheduleEvents.find(e => e.status === 'Aktiv');
      
      // Lade Checkliste für aktive Gruppe, falls vorhanden
      if (activeEvent?.group?.id) {
        try {
          const checklistResult = await callerService.getChecklist(activeEvent.group.id);
          if (checklistResult.success && checklistResult.data) {
            const groupChecklist = new Map();
            checklistResult.data.forEach(item => {
              groupChecklist.set(item.participantId, item);
            });
            setChecklistData(prev => {
              const newMap = new Map(prev);
              newMap.set(activeEvent.group.id, groupChecklist);
              return newMap;
            });
          }
        } catch (error) {
          console.error('Fehler beim Laden der Checkliste für aktive Gruppe:', error);
        }
      }
      
      setCurrentEvent(activeEvent || null);
      
      // Finde alle Events mit Status "Vorbereitung"
      const prepEvents = scheduleEvents.filter(e => e.status === 'Vorbereitung');
      setPreparationEvents(prepEvents);
      
      // Lade Teilnehmer für Vorbereitungsgruppen und aktive Gruppe
      const eventsToLoad = activeEvent ? [...prepEvents, activeEvent] : prepEvents;
      
      if (eventsToLoad.length > 0) {
        const participantsResult = await participantsService.getAllParticipants();
        if (participantsResult.success) {
          const allParticipants = participantsResult.data || [];
          const eventsWithParticipants = eventsToLoad.map(event => {
            if (event.group?.id) {
              const eventParticipants = allParticipants.filter(p => {
                if (!p.groups || !Array.isArray(p.groups)) return false;
                return p.groups.some(g => g.id === event.group.id);
              });
              
              // Sortiere nach Startnummer
              eventParticipants.sort((a, b) => {
                const aGroup = a.groups.find(g => g.id === event.group.id);
                const bGroup = b.groups.find(g => g.id === event.group.id);
                const aStartNumber = aGroup ? aGroup.startNumber : 999;
                const bStartNumber = bGroup ? bGroup.startNumber : 999;
                return aStartNumber - bStartNumber;
              });
              
              return {
                ...event,
                participants: eventParticipants,
                participantCount: eventParticipants.length
              };
            }
            return {
              ...event,
              participants: [],
              participantCount: 0
            };
          });
          
          // Trenne Vorbereitungs- und aktive Events
          const prepWithParticipants = eventsWithParticipants.filter(e => e.status === 'Vorbereitung');
          setPreparationEventsWithParticipants(prepWithParticipants);
          
          const activeWithParticipants = eventsWithParticipants.find(e => e.status === 'Aktiv');
          if (activeWithParticipants) {
            setCurrentEvent(activeWithParticipants);
          }
        } else {
          setPreparationEventsWithParticipants(prepEvents.map(e => ({ ...e, participants: [], participantCount: 0 })));
        }
      } else {
        setPreparationEventsWithParticipants([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const handleCheckboxChange = async (groupId, participantId, checked) => {
    // Optimistische UI-Aktualisierung - aktualisiere sofort den lokalen State
    const groupChecklist = checklistData.get(groupId) || new Map();
    const currentItem = groupChecklist.get(participantId) || {};
    
    // Erstelle optimistisches Update
    const optimisticUpdate = {
      ...currentItem,
      participantId,
      checkedByCaller: checked,
      checkedAt: checked ? (currentItem.checkedAt || new Date().toISOString()) : null,
      isLate: currentItem.isLate || false,
      lateCheckedAt: currentItem.lateCheckedAt || null,
      didNotStart: currentItem.didNotStart || false
    };
    
    // Aktualisiere lokalen State sofort
    const newChecklistData = new Map(checklistData);
    groupChecklist.set(participantId, optimisticUpdate);
    newChecklistData.set(groupId, groupChecklist);
    setChecklistData(newChecklistData);
    
    // Auch in Local Storage speichern (für Offline-Support)
    saveChecklistItem(groupId, participantId, checked);
    
    try {
      // Speichere im Backend (im Hintergrund)
      let result;
      if (checked) {
        result = await callerService.checkParticipant(groupId, participantId);
      } else {
        result = await callerService.uncheckParticipant(groupId, participantId);
      }
      
      if (!result.success) {
        throw new Error(result.error || 'Fehler beim Aktualisieren der Checkliste');
      }
      
      // Aktualisiere lokalen State mit Backend-Daten (falls sich etwas geändert hat)
      const backendData = result.data || {};
      const updatedGroupChecklist = newChecklistData.get(groupId) || new Map();
      updatedGroupChecklist.set(participantId, {
        ...updatedGroupChecklist.get(participantId),
        participantId,
        checkedByCaller: checked,
        checkedAt: backendData.checkedAt || (checked ? new Date().toISOString() : null),
        isLate: backendData.isLate || false,
        lateCheckedAt: backendData.lateCheckedAt || null,
        didNotStart: backendData.didNotStart || false
      });
      
      const finalChecklistData = new Map(newChecklistData);
      finalChecklistData.set(groupId, updatedGroupChecklist);
      setChecklistData(finalChecklistData);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Checkliste:', error);
      // Bei Fehler: Revertiere zu vorherigem Zustand
      const revertedGroupChecklist = checklistData.get(groupId) || new Map();
      const revertedItem = revertedGroupChecklist.get(participantId);
      if (revertedItem) {
        const revertedChecklistData = new Map(checklistData);
        revertedGroupChecklist.set(participantId, revertedItem);
        revertedChecklistData.set(groupId, revertedGroupChecklist);
        setChecklistData(revertedChecklistData);
      }
    }
  };

  // Berechne Anzahl abgehakter Athleten für eine Gruppe
  const getCheckedCount = (groupId, participants) => {
    return participants.filter(p => isParticipantChecked(groupId, p.id)).length;
  };

  // Wenn keine venue ausgewählt ist, zeige Flächenauswahl
  if (!assignedVenueFromUser && !selectedVenue) {
    return (
      <TailwindPageWrapper>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto mt-16">
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
                {t("caller.selectVenue.title", "Fläche auswählen")}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {t("caller.selectVenue.subtitle", "Bitte wählen Sie die Fläche aus, für die Sie die Athleten-Anwesenheit verwalten möchten.")}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => setSelectedVenue("Fläche A")}
                  className="w-full px-6 py-4 bg-white dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 rounded-lg text-left hover:border-primary dark:hover:border-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors"
                >
                  <div className="font-semibold text-slate-900 dark:text-slate-100">Fläche A</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {t("caller.selectVenue.venueA", "Athleten-Anwesenheit für Fläche A verwalten")}
                  </div>
                </button>
                <button
                  onClick={() => setSelectedVenue("Fläche B")}
                  className="w-full px-6 py-4 bg-white dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 rounded-lg text-left hover:border-primary dark:hover:border-primary-light hover:bg-primary/5 dark:hover:bg-primary-light/10 transition-colors"
                >
                  <div className="font-semibold text-slate-900 dark:text-slate-100">Fläche B</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {t("caller.selectVenue.venueB", "Athleten-Anwesenheit für Fläche B verwalten")}
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </TailwindPageWrapper>
    );
  }

  return (
    <TailwindPageWrapper>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {t("caller.title", "Caller - Athleten-Anwesenheit")}
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {t("caller.subtitle", "Verwalten Sie die Anwesenheit der Athleten für kommende Gruppen")}
            </p>
          </div>

          {/* Flächenauswahl, falls nicht zugewiesen */}
          {!assignedVenueFromUser && (
            <div className="mb-6 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t("caller.selectVenue", "Fläche auswählen")}
              </label>
              <select
                value={selectedVenue}
                onChange={(e) => setSelectedVenue(e.target.value)}
                className="w-full md:w-auto px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              >
                <option value="">{t("caller.selectVenuePlaceholder", "Fläche wählen...")}</option>
                <option value="Fläche A">{t("caller.venueA", "Fläche A")}</option>
                <option value="Fläche B">{t("caller.venueB", "Fläche B")}</option>
              </select>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">Lade Daten...</p>
            </div>
          ) : (
            <>
              {/* Aktuelles Event */}
              {currentEvent ? (
                <div className="mb-8">
                  <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                    <div className="bg-slate-50 dark:bg-slate-700/50 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                            {t("caller.currentEvent", "Aktuelles Event")}
                          </h2>
                          <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
                            {currentEvent.group?.name || currentEvent.category} • {formatDate(currentEvent.startTime)} • {formatTime(currentEvent.startTime)} - {formatTime(currentEvent.endTime)}
                          </p>
                        </div>
                        <div className="bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-full">
                          <span className="text-green-700 dark:text-green-300 text-sm font-medium">
                            {t("caller.status.active", "Aktiv")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      {currentEvent.participants && currentEvent.participants.length > 0 ? (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                            {t("caller.lateParticipants", "Nachzügler können nachträglich abgehakt werden:")}
                          </p>
                          {currentEvent.participants.map((participant) => {
                            const participantGroup = participant.groups?.find(g => g.id === currentEvent.group.id);
                            const startNumber = participantGroup?.startNumber || '-';
                            const nationCode = participant.nation?.code || participant.nationCode || 'DE';
                            const isChecked = isParticipantChecked(currentEvent.group.id, participant.id);
                            const groupChecklist = checklistData.get(currentEvent.group.id);
                            const checklistItem = groupChecklist?.get(participant.id);
                            const isLate = checklistItem?.isLate || false;
                            
                            return (
                              <div
                                key={participant.id}
                                onClick={() => handleCheckboxChange(currentEvent.group.id, participant.id, !isChecked)}
                                className={`flex items-center gap-4 p-3 rounded-lg border transition-colors cursor-pointer ${
                                  isChecked
                                    ? isLate
                                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                                      : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                    : 'bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'
                                }`}
                              >
                                <div className="relative flex-shrink-0">
                                  <div
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                      isChecked
                                        ? 'bg-primary dark:bg-primary-light border-primary dark:border-primary-light'
                                        : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'
                                    }`}
                                  >
                                    {isChecked && (
                                      <i className="fas fa-check text-white text-xs"></i>
                                    )}
                                  </div>
                                </div>
                                <div className="flex-1 flex items-center gap-3">
                                  <img
                                    src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                    alt={participant.nation?.name || participant.nation}
                                    className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                      {participant.name}
                                      {isLate && (
                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                          <i className="fas fa-clock mr-1"></i>
                                          {t("caller.late", "Verspätet")}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                      {t("caller.participantId", "ID")}: {participant.participantNumber || participant.id} • {participant.nation?.name || participant.nation} • {t("caller.startNumber", "Startnummer")}: #{startNumber}
                                    </div>
                                  </div>
                                </div>
                                {isChecked && (
                                  <i className="fas fa-check-circle text-green-600 dark:text-green-400"></i>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-slate-600 dark:text-slate-400">
                          {t("caller.currentEventInfo", "Diese Gruppe läuft derzeit. Die nächsten Gruppen sind in Vorbereitung.")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-8 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-12">
                  <div className="text-center">
                    <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-clock text-slate-400 dark:text-slate-500 text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                      {t("caller.noActiveEvent", "Kein aktives Event")}
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400">
                      {t("caller.noActiveEventDesc", "Es läuft derzeit kein Event auf dieser Fläche")}
                    </p>
                  </div>
                </div>
              )}

              {/* Gruppen in Vorbereitung */}
              {preparationEventsWithParticipants.length > 0 ? (
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
                    {t("caller.preparationGroups", "Gruppen in Vorbereitung")}
                  </h2>
                  <div className="space-y-6">
                    {preparationEventsWithParticipants.map((event) => {
                      const checkedCount = getCheckedCount(event.group.id, event.participants);
                      const totalCount = event.participants.length;
                      
                      return (
                        <div
                          key={event.id}
                          className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
                        >
                          <div className="bg-orange-50 dark:bg-orange-900/30 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                  {event.group?.name || event.category}
                                </h3>
                                <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
                                  {formatDate(event.startTime)} • {formatTime(event.startTime)} - {formatTime(event.endTime)}
                                </p>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <div className="text-sm text-slate-500 dark:text-slate-400">
                                    {t("caller.checkedCount", "Abgehakt")}
                                  </div>
                                  <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                    {checkedCount} / {totalCount}
                                  </div>
                                </div>
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                                  <i className="fas fa-hourglass-half mr-1.5"></i>
                                  {t("caller.status.preparation", "Vorbereitung")}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="p-6">
                            {event.participants.length > 0 ? (
                              <div className="space-y-2">
                                {event.participants.map((participant) => {
                                  const participantGroup = participant.groups?.find(g => g.id === event.group.id);
                                  const startNumber = participantGroup?.startNumber || '-';
                                  const nationCode = participant.nation?.code || participant.nationCode || 'DE';
                                  const isChecked = isParticipantChecked(event.group.id, participant.id);
                                  const didNotStart = participantGroup?.didNotStart || false;
                                  
                                  return (
                                    <div
                                      key={participant.id}
                                      onClick={() => handleCheckboxChange(event.group.id, participant.id, !isChecked)}
                                      className={`flex items-center gap-4 p-3 rounded-lg border transition-colors cursor-pointer ${
                                        didNotStart
                                          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 opacity-60'
                                          : isChecked
                                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                            : 'bg-slate-50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'
                                      }`}
                                    >
                                      <div className="relative flex-shrink-0">
                                        <div
                                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                            isChecked
                                              ? 'bg-primary dark:bg-primary-light border-primary dark:border-primary-light'
                                              : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'
                                          }`}
                                        >
                                          {isChecked && (
                                            <i className="fas fa-check text-white text-xs"></i>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex-1 flex items-center gap-3">
                                        <img
                                          src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                                          alt={participant.nation?.name || participant.nation}
                                          className="w-6 h-6 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                        <div className="flex-1">
                                          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                            {participant.name}
                                          </div>
                                          <div className="text-xs text-slate-500 dark:text-slate-400">
                                            {t("caller.participantId", "ID")}: {participant.participantNumber || participant.id} • {participant.nation?.name || participant.nation} • {t("caller.startNumber", "Startnummer")}: #{startNumber}
                                          </div>
                                        </div>
                                      </div>
                                      {isChecked && (
                                        <i className="fas fa-check-circle text-green-600 dark:text-green-400"></i>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                                {t("caller.noParticipants", "Keine Teilnehmer für diese Gruppe")}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-12">
                  <div className="text-center">
                    <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-hourglass-half text-slate-400 dark:text-slate-500 text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                      {t("caller.noPreparationGroups", "Keine Gruppen in Vorbereitung")}
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400">
                      {t("caller.noPreparationGroupsDesc", "Derzeit sind keine Gruppen in Vorbereitung")}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </TailwindPageWrapper>
  );
};

export default CallerView;

